import { hydratePassSelectionsFromOrder, useCheckoutHydration } from '@/hooks/useCheckoutHydration';
import { useCalculateOffers, type DomainOffersEntry } from '@/pages/useCalculateOffers';
import { centsToMoney, pushDataLayerOnce } from '@/services/gtm-ecommerce.service';
import type { CheckoutPassSelectionDto, OrderItemDetailsPayload } from '@/services/orders.service';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { isValidBirthDate, isValidEmail, isValidPhone } from '@/utils/booking';
import {
  isBirthDateMatchingAnyCategory,
  type DomainRestriction,
} from '@/utils/domain-restrictions';
import {
  applyCalculatedPeopleItemsToSlots,
  expandPassSelections,
  expandSelectedOffers,
  reconcileSlotsWithCalculatedOffers,
  syncSkierSlots,
  type SkierFormData,
  type SkierSlot,
} from '@/utils/skier-slots';
import { calculatePromoTotals } from '@/utils/promo-pricing';
import { buildSkicardAddonQueuesByDomainAge, takeSkicardAddonPrice } from '@/utils/skicard-pricing';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function normalizeCategory(s: string): string {
  return s.trim().toLowerCase();
}

function removeSlotAtIndex(
  selections: CheckoutPassSelectionDto[],
  slotIndex: number
): CheckoutPassSelectionDto[] {
  let cumulative = 0;
  const result: CheckoutPassSelectionDto[] = [];

  for (const sel of selections) {
    const qty = Math.max(0, sel.quantity);
    if (slotIndex >= cumulative && slotIndex < cumulative + qty) {
      const newQty = qty - 1;
      if (newQty > 0) result.push({ ...sel, quantity: newQty });
    } else {
      result.push(sel);
    }
    cumulative += qty;
  }

  return result;
}

export type { SkierFormData, SkierSlot };

export type BookingEcommerceItem = {
  itemId: string;
  itemName: string;
  priceInCents: number;
  quantity: number;
  currency: string;
};

function isSkierComplete(data: SkierFormData): boolean {
  const fn = data.firstName.trim();
  const ln = data.lastName.trim();
  const birthDate = data.birthDate.trim();
  return fn.length > 0 && ln.length > 0 && isValidBirthDate(birthDate);
}

export function useBookingDetails() {
  const selectedPassQuantities = useBookSkiPassStore((s) => s.selectedPassQuantities);
  const stepThreeFormFieldsValues = useBookSkiPassStore((s) => s.stepThreeFormFieldsValues);
  const updateStepThreeFormFields = useBookSkiPassStore((s) => s.updateStepThreeFormFields);
  const updateStepThreeSkierForm = useBookSkiPassStore((s) => s.updateStepThreeSkierForm);

  const { order: checkout, loading: checkoutLoading, error: checkoutError } = useCheckoutHydration({
    hydrateStepThree: true,
    requiredSession: true,
  });

  const {
    dataByDomain,
    loading: offersLoading,
    error: offersError,
  } = useCalculateOffers({
    passSelections: checkout?.passSelections,
  });

  const [consent, setConsent] = useState(false);
  const skierSlotsRef = useRef<SkierSlot[]>([]);

  const effectivePassSelections = useMemo(() => {
    return stepThreeFormFieldsValues.passSelectionOverrides ?? checkout?.passSelections ?? [];
  }, [stepThreeFormFieldsValues.passSelectionOverrides, checkout?.passSelections]);

  const { customerEmail, customerPhone, skierForms } = stepThreeFormFieldsValues;

  const setCustomerEmail = useCallback(
    (value: string) => {
      updateStepThreeFormFields({ customerEmail: value });
    },
    [updateStepThreeFormFields]
  );

  const setCustomerPhone = useCallback(
    (value: string) => {
      updateStepThreeFormFields({ customerPhone: value });
    },
    [updateStepThreeFormFields]
  );

  useEffect(() => {
    if (!checkout?.passSelections?.length || dataByDomain.length === 0) return;
    hydratePassSelectionsFromOrder(checkout, dataByDomain);
  }, [checkout, dataByDomain]);

  const skierSlots = useMemo(() => {
    const fromCheckoutSelections = expandPassSelections(effectivePassSelections).map(
      (slot, index) => ({
        ...slot,
        birthDate: skierForms[index]?.birthDate?.trim() || undefined,
      })
    );

    const alignWithExistingForms = (slots: SkierSlot[]): SkierSlot[] => {
      return syncSkierSlots(skierSlotsRef.current, slots, skierForms);
    };

    if (fromCheckoutSelections.length > 0) {
      const fromPeople = applyCalculatedPeopleItemsToSlots(fromCheckoutSelections, dataByDomain);
      const reconciled = reconcileSlotsWithCalculatedOffers(fromPeople, dataByDomain);
      const aligned = alignWithExistingForms(reconciled);
      skierSlotsRef.current = aligned;
      return aligned;
    }

    const fromSelectedOffers = expandSelectedOffers(dataByDomain, selectedPassQuantities).map(
      (slot, index) => ({
        ...slot,
        birthDate: skierForms[index]?.birthDate?.trim() || undefined,
      })
    );
    if (fromSelectedOffers.length > 0) {
      const fromPeople = applyCalculatedPeopleItemsToSlots(fromSelectedOffers, dataByDomain);
      const reconciled = reconcileSlotsWithCalculatedOffers(fromPeople, dataByDomain);
      const aligned = alignWithExistingForms(reconciled);
      skierSlotsRef.current = aligned;
      return aligned;
    }

    skierSlotsRef.current = [];

    return [];
  }, [effectivePassSelections, dataByDomain, selectedPassQuantities, skierForms]);

  useEffect(() => {
    if (skierSlots.length === 0) return;
    if (skierForms.length === skierSlots.length) return;

    const next = [...skierForms];
    while (next.length < skierSlots.length) {
      next.push({ skipassId: undefined, firstName: '', lastName: '', birthDate: '' });
    }

    updateStepThreeFormFields({ skierForms: next.slice(0, skierSlots.length) });
  }, [updateStepThreeFormFields, skierForms, skierSlots.length]);

  const originalSlotCategories = useMemo(() => {
    return expandPassSelections(checkout?.passSelections ?? []).map((s) => s.ageCategory);
  }, [checkout?.passSelections]);

  const changedCategories = useMemo(() => {
    return skierSlots.map((slot, i) => {
      const original = originalSlotCategories[i];
      return original != null && normalizeCategory(original) !== normalizeCategory(slot.ageCategory);
    });
  }, [skierSlots, originalSlotCategories]);

  const orderedSelectionsRef = useRef<string | null>(null);

  useEffect(() => {
    if (skierSlots.length === 0) return;
    if (!changedCategories.some(Boolean)) return;

    const countMap = new Map<string, CheckoutPassSelectionDto>();
    const ordered: CheckoutPassSelectionDto[] = [];

    for (const slot of skierSlots) {
      const key = `${String(slot.domain.id)}|${slot.ageCategory}|${String(slot.passOfferId ?? '')}`;
      const existing = countMap.get(key);
      if (existing) {
        existing.quantity += 1;
      } else {
        const entry: CheckoutPassSelectionDto = {
          ageCategory: slot.ageCategory,
          domain: slot.domain,
          passOfferId: slot.passOfferId,
          passTitle: slot.passTitle,
          quantity: 1,
        };
        countMap.set(key, entry);
        ordered.push(entry);
      }
    }

    const orderedKey = JSON.stringify(ordered);
    if (orderedSelectionsRef.current === orderedKey) return;
    orderedSelectionsRef.current = orderedKey;

    updateStepThreeFormFields({ passSelectionOverrides: ordered });
  }, [skierSlots, changedCategories, updateStepThreeFormFields]);

  const domainRestrictionsMap = useMemo(() => {
    const map = new Map<number, DomainRestriction[]>();
    for (const { domain } of dataByDomain) {
      if (domain.restrictions?.length) {
        map.set(domain.pk, domain.restrictions as DomainRestriction[]);
      }
    }
    return map;
  }, [dataByDomain]);

  const startDate = checkout?.tripDetails?.startDate ?? '';

  const invalidAgeBySlotIndex = useMemo(() => {
    return skierSlots.map((slot, i) => {
      const birthDate = skierForms[i]?.birthDate?.trim() ?? '';
      if (!birthDate || !isValidBirthDate(birthDate)) return false;
      const restrictions = domainRestrictionsMap.get(slot.domain.id) ?? [];
      return !isBirthDateMatchingAnyCategory(birthDate, restrictions, startDate);
    });
  }, [skierSlots, skierForms, domainRestrictionsMap, startDate]);

  const skierDetailsReadyForPayment = useMemo(() => {
    if (skierSlots.length === 0) return false;
    const emptyForm: SkierFormData = { firstName: '', lastName: '', birthDate: '' };
    let hasAtLeastOneOrderSkier = false;
    for (let i = 0; i < skierSlots.length; i++) {
      if (invalidAgeBySlotIndex[i]) continue;
      if (!isSkierComplete(skierForms[i] ?? emptyForm)) return false;
      hasAtLeastOneOrderSkier = true;
    }
    return hasAtLeastOneOrderSkier;
  }, [skierSlots, skierForms, invalidAgeBySlotIndex]);

  const completedCount = useMemo(() => {
    return skierForms.filter(isSkierComplete).length;
  }, [skierForms]);

  const allSkiersComplete = skierSlots.length > 0 && completedCount === skierSlots.length;
  const hasValidCustomerContacts =
    isValidEmail(customerEmail.trim()) && isValidPhone(customerPhone.trim());
  const canSubmit =
    hasValidCustomerContacts &&
    consent &&
    skierDetailsReadyForPayment;

  const removeSkierSlot = useCallback(
    (slotIndex: number) => {
      const current = stepThreeFormFieldsValues.passSelectionOverrides ?? checkout?.passSelections ?? [];
      const nextForms = [...skierForms];
      nextForms.splice(slotIndex, 1);
      updateStepThreeFormFields({
        passSelectionOverrides: removeSlotAtIndex(current, slotIndex),
        skierForms: nextForms,
      });
    },
    [stepThreeFormFieldsValues.passSelectionOverrides, checkout?.passSelections, skierForms, updateStepThreeFormFields]
  );

  const buildOrderItems = useCallback((): OrderItemDetailsPayload[] => {
    const trip = checkout?.tripDetails;
    if (!trip) return [];

    const startDate = trip.startDate;
    const days = trip.skiDays;
    if (!startDate || days < 1) return [];

    const originalSelections = expandPassSelections(effectivePassSelections);

    const items: OrderItemDetailsPayload[] = [];
    for (let i = 0; i < skierSlots.length; i++) {
      if (invalidAgeBySlotIndex[i]) continue;
      const slot = skierSlots[i];
      const form = skierForms[i] ?? {
        skipassId: undefined,
        firstName: '',
        lastName: '',
        birthDate: '',
      };
      if (!slot) continue;
      const originalAgeCategory = originalSelections[i]?.ageCategory;
      const ageCategoryChanged =
        originalAgeCategory != null &&
        normalizeCategory(originalAgeCategory) !== normalizeCategory(slot.ageCategory);

      items.push({
        skipassId: ageCategoryChanged ? undefined : form.skipassId,
        ageCategory: slot.ageCategory,
        birthDate: form.birthDate || '2000-01-01',
        firstName: form.firstName || '',
        lastName: form.lastName || '',
        startDate,
        days,
        domain: slot.domain,
      });
    }
    return items;
  }, [checkout?.tripDetails, effectivePassSelections, skierSlots, skierForms, invalidAgeBySlotIndex]);

  const { grandTotal, subTotal, currency, ecommerceItems } = useMemo(() => {
    const domains: DomainOffersEntry[] = dataByDomain;
    const priceBySlotKey = new Map<string, { unitPrice: number; currency: string }>();
    const skicardAddonQueues = buildSkicardAddonQueuesByDomainAge(dataByDomain);

    for (const { domain, offers } of domains) {
      for (const item of offers.items) {
        const unitPrice = item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price;
        const keyByPassPk =
          item.pass_pk != null
            ? `${String(domain.pk)}|${String(item.pass_pk)}`
            : `${String(domain.pk)}|${item.customer_type}|${item.title}`;
        priceBySlotKey.set(keyByPassPk, { unitPrice, currency: offers.currency });
      }
    }

    let curr = 'EUR';
    const itemsById = new Map<string, BookingEcommerceItem>();
    const promoItems: { unitPrice: number; domainId: number }[] = [];

    const validSlots = skierSlots.filter((_, i) => !invalidAgeBySlotIndex[i]);

    for (const slot of validSlots) {
      const primaryKey =
        slot.passOfferId != null
          ? `${String(slot.domain.id)}|${String(slot.passOfferId)}`
          : `${String(slot.domain.id)}|${slot.ageCategory}|${slot.passTitle}`;
      const priceEntry =
        priceBySlotKey.get(primaryKey) ??
        priceBySlotKey.get(`${String(slot.domain.id)}|${slot.ageCategory}|${slot.passTitle}`);
      if (!priceEntry) continue;

      const unitPriceWithSkicard =
        priceEntry.unitPrice +
        takeSkicardAddonPrice(skicardAddonQueues, slot.domain.id, slot.ageCategory);

      const itemId =
        slot.passOfferId != null
          ? String(slot.passOfferId)
          : `${String(slot.domain.id)}:${slot.ageCategory}:${slot.passTitle}`;
      const itemName = `${slot.domain.name} ${slot.passTitle}`.trim();

      const existing = itemsById.get(itemId);
      if (existing) {
        existing.quantity += 1;
      } else {
        itemsById.set(itemId, {
          itemId,
          itemName,
          priceInCents: unitPriceWithSkicard,
          quantity: 1,
          currency: priceEntry.currency,
        });
      }

      promoItems.push({ unitPrice: unitPriceWithSkicard, domainId: slot.domain.id });
      curr = priceEntry.currency;
    }

    const totals = calculatePromoTotals(promoItems, checkout?.promoCodeInfo);

    return {
      grandTotal: totals.grandTotal,
      subTotal: totals.subtotal,
      currency: curr,
      ecommerceItems: [...itemsById.values()],
    };
  }, [dataByDomain, skierSlots, invalidAgeBySlotIndex, checkout?.promoCodeInfo]);

  useEffect(() => {
    if (!checkout || ecommerceItems.length === 0) return;
    pushDataLayerOnce(
      `add_to_cart:${String(checkout.orderId)}`,
      {
        event: 'add_to_cart',
        ecommerce: {
          currency,
          items: ecommerceItems.map((item) => ({
            item_id: item.itemId,
            item_name: item.itemName,
            price: centsToMoney(item.priceInCents),
            quantity: item.quantity,
            currency: item.currency,
          })),
        },
      },
      {
        booking_ref: checkout.reference,
        promocode: checkout.promoCode,
      }
    );
  }, [checkout, ecommerceItems, currency]);

  return {
    checkout,
    loading: checkoutLoading || offersLoading,
    offersLoading,
    error: checkoutError ?? offersError,
    customerEmail,
    setCustomerEmail,
    customerPhone,
    setCustomerPhone,
    skierSlots,
    skierForms,
    updateStepThreeSkierForm,
    consent,
    setConsent,
    completedCount,
    allSkiersComplete,
    canSubmit,
    buildOrderItems,
    grandTotal,
    subTotal,
    currency,
    ecommerceItems,
    changedCategories,
    domainRestrictionsMap,
    invalidAgeBySlotIndex,
    removeSkierSlot,
    promoCode: checkout?.promoCode,
    promoCodeInfo: checkout?.promoCodeInfo,
    skierDetailsReadyForPayment,
  };
}
